import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Filter } from 'lucide-react'
import { glDetailColumnLabel, previewRowsAsObjects } from '../quickBooksGlDetailShared'
import { formatGlNumber, type CfoGlDetailQueryState, type CfoGlDetailTotals } from './cfoGlDetailQuery'
import {
  buildCfoGlProfileFieldSql,
  cfoGlProfileDateSpansYears,
  cfoGlProfileEntryLabel,
  cfoGlProfileFieldKind,
  cfoGlProfileFiltersToPredicates,
  cfoGlProfileNumericBinBounds,
  parseCfoGlProfileFieldRows,
  type CfoGlProfileDateGrain,
  type CfoGlProfileFieldData,
  type CfoGlProfileFilters,
  type CfoGlProfileSortMode,
} from './cfoGlProfileModel'

const PROFILE_QUERY_TIMEOUT_MS = 60000
const PROFILE_PREVIEW_ROW_LIMIT = 900
const COLLAPSE_STORAGE_KEY = 'cfo_gl_detail_profiles_collapsed'

const SORT_CYCLE: CfoGlProfileSortMode[] = ['count_desc', 'count_asc', 'value_asc', 'value_desc']
const SORT_GLYPH: Record<CfoGlProfileSortMode, string> = {
  count_desc: '#↓',
  count_asc: '#↑',
  value_asc: 'A↓',
  value_desc: 'A↑',
}
const SORT_TITLE: Record<CfoGlProfileSortMode, string> = {
  count_desc: 'Sorted by count (desc). Click to change.',
  count_asc: 'Sorted by count (asc). Click to change.',
  value_asc: 'Sorted by value (asc). Click to change.',
  value_desc: 'Sorted by value (desc). Click to change.',
}

type WorkerLike = { duckdb_run_sql: (sql: string, opts?: Record<string, any>) => Promise<any> }

type FieldSlot = CfoGlProfileFieldData | 'error' | undefined

type TooltipState = {
  x: number
  y: number
  title: string
  sub: string
}

export type CfoGlProfilePaneProps = {
  workerClient: WorkerLike | null
  duckDbOpts?: Record<string, any> | null
  availableColumns: string[]
  visibleColumns: string[]
  /** Executable page query state; its rawPredicates are rebuilt per card for faceting. */
  queryState: CfoGlDetailQueryState
  totals: CfoGlDetailTotals
  totalsNumericColumns: string[]
  profileFilters: CfoGlProfileFilters
  onProfileFiltersChange: (next: CfoGlProfileFilters) => void
  /** Columns with a regular Filters-row filter (profile filters are tracked internally). */
  activeFilterColumns: Set<string>
  onClearColumnFilter: (column: string) => void
}

function boundedCacheSet<K, V>(cache: Map<K, V>, key: K, value: V, limit = 64): void {
  if (cache.has(key)) cache.delete(key)
  cache.set(key, value)
  while (cache.size > limit) {
    const first = cache.keys().next()
    if (first.done) break
    cache.delete(first.value)
  }
}

function profileScopeKey(state: CfoGlDetailQueryState): string {
  // rawPredicates are intentionally excluded: the pane rebuilds them per card
  // from profileFilters so each card facets against the other cards only.
  return JSON.stringify({
    search: state.search,
    startDate: state.startDate,
    endDate: state.endDate,
    filter: state.filter || null,
    filters: state.filters || [],
    scopes: state.scopes || [],
  })
}

function defaultSortMode(kind: ReturnType<typeof cfoGlProfileFieldKind>): CfoGlProfileSortMode {
  return kind === 'categorical' ? 'count_desc' : 'value_asc'
}

export function CfoGlProfilePane(props: CfoGlProfilePaneProps) {
  const {
    workerClient,
    duckDbOpts,
    availableColumns,
    visibleColumns,
    queryState,
    totals,
    totalsNumericColumns,
    profileFilters,
    onProfileFiltersChange,
    activeFilterColumns,
    onClearColumnFilter,
  } = props
  const [dataByColumn, setDataByColumn] = useState<Record<string, FieldSlot>>({})
  const [grainByColumn, setGrainByColumn] = useState<Record<string, CfoGlProfileDateGrain>>({})
  const [sortByColumn, setSortByColumn] = useState<Record<string, CfoGlProfileSortMode>>({})
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const fieldCacheRef = useRef<Map<string, CfoGlProfileFieldData>>(new Map())
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const profiledColumns = useMemo(() => visibleColumns.filter(Boolean), [visibleColumns])
  const profiledColumnsKey = profiledColumns.join('|')
  const scopeKey = useMemo(() => profileScopeKey(queryState), [queryState])
  const profileFiltersKey = useMemo(() => JSON.stringify(profileFilters), [profileFilters])
  const grainKey = useMemo(
    () => profiledColumns.map((column) => `${column}:${grainByColumn[column] || 'week'}`).join('|'),
    [grainByColumn, profiledColumns],
  )

  // One aggregate per visible field, run sequentially so the DuckDB worker keeps
  // servicing the rows/totals queries between fields. Each card's scope carries
  // every OTHER card's selection (faceted search): its own values stay visible
  // so the user can extend or shrink the selection. Numeric cards with an
  // active selection keep the bin geometry the selection was made against.
  useEffect(() => {
    if (!workerClient || !availableColumns.length || !profiledColumns.length || collapsed) return
    let cancelled = false
    void (async () => {
      for (const column of profiledColumns) {
        const kind = cfoGlProfileFieldKind(column)
        const grain = kind === 'date' ? (grainByColumn[column] || 'week') : undefined
        const ownSelection = profileFilters[column]
        const frozenBins = kind === 'numeric' && ownSelection && Number.isFinite(ownSelection.binLo) && Number.isFinite(ownSelection.binHi)
        const sql = buildCfoGlProfileFieldSql({
          state: { ...queryState, rawPredicates: cfoGlProfileFiltersToPredicates(profileFilters, column) },
          availableColumns,
          column,
          grain,
          binLo: frozenBins ? ownSelection.binLo : undefined,
          binHi: frozenBins ? ownSelection.binHi : undefined,
        })
        const cached = fieldCacheRef.current.get(sql)
        if (cached) {
          if (!cancelled && mountedRef.current) setDataByColumn((current) => ({ ...current, [column]: cached }))
          continue
        }
        try {
          const result = await workerClient.duckdb_run_sql(sql, {
            ...(duckDbOpts || {}),
            previewRowLimit: PROFILE_PREVIEW_ROW_LIMIT,
            timeoutMs: PROFILE_QUERY_TIMEOUT_MS,
            caller: 'QuickBooksCfoCore:gl_detail_profile_field',
          })
          if (cancelled || !mountedRef.current) return
          const parsed = parseCfoGlProfileFieldRows({
            column,
            kind,
            grain,
            rows: previewRowsAsObjects(result),
            fixedBinLo: frozenBins ? ownSelection.binLo : undefined,
            fixedBinHi: frozenBins ? ownSelection.binHi : undefined,
          })
          boundedCacheSet(fieldCacheRef.current, sql, parsed)
          setDataByColumn((current) => ({ ...current, [column]: parsed }))
        } catch {
          if (cancelled || !mountedRef.current) return
          setDataByColumn((current) => ({ ...current, [column]: 'error' }))
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerClient, duckDbOpts, availableColumns, profiledColumnsKey, scopeKey, profileFiltersKey, grainKey, collapsed])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current
      try {
        window.localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? '1' : '0')
      } catch {
        // Storage can be unavailable (private mode); the toggle still works for the session.
      }
      return next
    })
  }, [])

  const toggleKey = useCallback((column: string, data: CfoGlProfileFieldData, key: string) => {
    const current = profileFilters[column]
    if (!current) {
      onProfileFiltersChange({
        ...profileFilters,
        [column]: {
          column,
          kind: data.kind,
          keys: [key],
          grain: data.grain,
          binLo: data.binLo,
          binHi: data.binHi,
        },
      })
      return
    }
    const keys = current.keys.includes(key)
      ? current.keys.filter((existing) => existing !== key)
      : [...current.keys, key]
    const next = { ...profileFilters }
    if (keys.length) next[column] = { ...current, keys }
    else delete next[column]
    onProfileFiltersChange(next)
  }, [onProfileFiltersChange, profileFilters])

  const clearAllProfileFilters = useCallback(() => {
    onProfileFiltersChange({})
  }, [onProfileFiltersChange])

  const profileFilterCount = Object.keys(profileFilters).length

  const totalsTip = useMemo(() => {
    const lines: Array<[string, string]> = [['Rows', totals.rowCount.toLocaleString()]]
    for (const column of totalsNumericColumns) {
      lines.push([glDetailColumnLabel(column), formatGlNumber(totals.totals[column] || 0)])
    }
    if (totals.minDate || totals.maxDate) {
      lines.push(['Dates', `${totals.minDate || '…'} to ${totals.maxDate || '…'}`])
    }
    return lines
  }, [totals, totalsNumericColumns])

  if (!profiledColumns.length) return null

  return (
    <section className="cfo-glp rise" aria-label="Field profiles">
      <div className="cfo-glp-toolbar">
        <span className="cfo-glp-count">
          {profiledColumns.length.toLocaleString()} fields · {totals.rowCount.toLocaleString()} rows
          <span className="cfo-glp-counttip" role="tooltip">
            {totalsTip.map(([label, value]) => (
              <span key={label} className="cfo-glp-counttip-line">
                <em>{label}</em>
                <strong>{value}</strong>
              </span>
            ))}
          </span>
        </span>
        <span className="cfo-glp-hint">Click values to filter · click again to remove</span>
        {profileFilterCount ? (
          <button type="button" className="cfo-glp-action" onClick={clearAllProfileFilters}>
            Clear {profileFilterCount} card {profileFilterCount === 1 ? 'filter' : 'filters'}
          </button>
        ) : null}
        <span className="cfo-glp-spacer" />
        <button type="button" className="cfo-glp-action cfo-glp-action-quiet" onClick={toggleCollapsed} aria-expanded={!collapsed}>
          {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          {collapsed ? 'Show profiles' : 'Hide profiles'}
        </button>
      </div>
      {!collapsed ? (
        <div className="cfo-glp-cards">
          {profiledColumns.map((column) => {
            const slot = dataByColumn[column]
            const kind = cfoGlProfileFieldKind(column)
            const sortMode = sortByColumn[column] || defaultSortMode(kind)
            const grain = grainByColumn[column] || 'week'
            const ownSelection = profileFilters[column]
            const selectedKeys = ownSelection?.keys || []
            const data = slot && slot !== 'error' ? slot : null
            const spansYears = data ? cfoGlProfileDateSpansYears(data) : false
            const entries = data ? [...data.entries] : []
            const labelFor = (entry: CfoGlProfileFieldData['entries'][number]) => (data ? cfoGlProfileEntryLabel(data, entry, spansYears) : entry.key)
            entries.sort((a, b) => {
              const byValue = kind === 'categorical'
                ? labelFor(a).localeCompare(labelFor(b))
                : (kind === 'numeric' ? Number(a.key) - Number(b.key) : a.key.localeCompare(b.key))
              if (sortMode === 'count_desc') return (b.count - a.count) || byValue
              if (sortMode === 'count_asc') return (a.count - b.count) || byValue
              return sortMode === 'value_asc' ? byValue : -byValue
            })
            const maxCount = Math.max(1, ...entries.map((entry) => entry.count), data?.moreRows || 0)
            const hasAnyFilter = selectedKeys.length > 0 || activeFilterColumns.has(column)
            return (
              <div className="cfo-glp-card" key={column}>
                <div className="cfo-glp-glyphrow">
                  <span className="cfo-glp-glyph">{kind === 'numeric' ? '#' : kind === 'date' ? '▦' : 'Abc'}</span>
                  {kind === 'date' ? (
                    <select
                      className="cfo-glp-grain"
                      aria-label={`${glDetailColumnLabel(column)} grain`}
                      value={grain}
                      onChange={(event) => {
                        const nextGrain = event.target.value as CfoGlProfileDateGrain
                        // Bin keys are grain-specific, so an existing selection can't survive the switch.
                        if (profileFilters[column]) {
                          const next = { ...profileFilters }
                          delete next[column]
                          onProfileFiltersChange(next)
                        }
                        setGrainByColumn((current) => ({ ...current, [column]: nextGrain }))
                      }}
                    >
                      <option value="day">Day</option>
                      <option value="week">Week</option>
                      <option value="month">Month</option>
                    </select>
                  ) : null}
                  <span className="cfo-glp-spacer" />
                  {hasAnyFilter ? (
                    <button
                      type="button"
                      className="cfo-glp-funnel"
                      onClick={() => onClearColumnFilter(column)}
                      title={`Clear filter on ${glDetailColumnLabel(column)}`}
                    >
                      <Filter size={11} /> filtered
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={sortMode === defaultSortMode(kind) ? 'cfo-glp-sort' : 'cfo-glp-sort cfo-glp-sort-active'}
                    title={SORT_TITLE[sortMode]}
                    onClick={() => setSortByColumn((current) => ({
                      ...current,
                      [column]: SORT_CYCLE[(SORT_CYCLE.indexOf(sortMode) + 1) % SORT_CYCLE.length],
                    }))}
                  >
                    {SORT_GLYPH[sortMode]}
                  </button>
                </div>
                <div className="cfo-glp-name">
                  {glDetailColumnLabel(column)}
                  <span className="cfo-glp-vcount">
                    {data ? (data.distinctCount + data.moreValues).toLocaleString() : slot === 'error' ? '—' : '…'}
                  </span>
                </div>
                <div className="cfo-glp-vlist">
                  {slot === 'error' ? (
                    <div className="cfo-glp-note">Profile unavailable</div>
                  ) : !data ? (
                    <div className="cfo-glp-note">Loading…</div>
                  ) : !entries.length ? (
                    <div className="cfo-glp-note">No values in scope</div>
                  ) : (
                    <>
                      {entries.map((entry) => {
                        const isSelected = selectedKeys.includes(entry.key)
                        const negBucket = data.kind === 'numeric' && cfoGlProfileNumericBinBounds(data, Number(entry.key)).start < 0
                        const classNames = ['cfo-glp-vrow']
                        if (isSelected) classNames.push('sel')
                        if (negBucket) classNames.push('neg')
                        const label = labelFor(entry)
                        return (
                          <button
                            type="button"
                            key={entry.key || '(blank)'}
                            className={classNames.join(' ')}
                            aria-pressed={isSelected}
                            onClick={() => toggleKey(column, data, entry.key)}
                            onMouseMove={(event) => {
                              setTooltip({
                                x: event.clientX,
                                y: event.clientY,
                                title: label,
                                sub: `${entry.count.toLocaleString()} rows · ${formatGlNumber(entry.amount)}${isSelected ? ' · filtered in' : ''}`,
                              })
                            }}
                            onMouseLeave={() => setTooltip(null)}
                          >
                            <span className="cfo-glp-track">
                              <span className="cfo-glp-base" style={{ width: `${(entry.count / maxCount) * 100}%` }} />
                              <span className="cfo-glp-lbl">{label}</span>
                              {isSelected ? <span className="cfo-glp-selmark" aria-hidden="true">✓</span> : null}
                            </span>
                          </button>
                        )
                      })}
                      {data.moreValues > 0 ? (
                        <div className="cfo-glp-vrow more">
                          <span className="cfo-glp-track">
                            <span className="cfo-glp-base" style={{ width: `${(data.moreRows / maxCount) * 100}%` }} />
                            <span className="cfo-glp-lbl">{data.moreValues.toLocaleString()} more values…</span>
                          </span>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
      {tooltip ? (
        <div
          className="cfo-glp-tipbox"
          style={{ left: Math.min(tooltip.x + 14, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 300), top: tooltip.y + 16 }}
        >
          <div className="cfo-glp-tipbox-title">{tooltip.title}</div>
          <div className="cfo-glp-tipbox-sub">{tooltip.sub}</div>
        </div>
      ) : null}
    </section>
  )
}

export default CfoGlProfilePane
